<template>
  <div>
    <b-button size="-small" variant="-secondary btn--progress" :disabled="disablePause" v-if="showPause" @click="handlePause">
      <b-spinner v-if="disablePause" small type="grow"></b-spinner>{{$t('printers.job.pause')}}
    </b-button>
    <b-button size="-small" variant="-secondary" :disabled="disableStart || !isBuildTrayIsClear" v-bind="startButtonAttributes" v-if="showPrint" @click="handlePrint">
      <b-spinner v-if="disableStart" small type="grow"></b-spinner>{{$t('printers.job.start')}}
    </b-button>
    <b-button size="-small" variant="-secondary" :disabled="disableResume" v-if="showResume" @click="handleResume">
      <b-spinner v-if="disableResume" small type="grow"></b-spinner>{{$t('printers.job.resume')}}
    </b-button>
    <b-button size="-small" variant="-secondary" :disabled="disableVirtualFinish" v-if="showVirtualFinish && isPrinting" @click="handleVirtualFinish">
      <b-spinner v-if="disableVirtualFinish" small type="grow"></b-spinner>{{$t('printers.job.finish')}}
    </b-button>
    <b-button size="-small" variant="-ternary btn--outline" v-if="showCancel && (isPrinting && !isIndustrialPrinter)" @click="handleOpenCancelJob">
      {{$t('printers.job.cancel')}}
    </b-button>
    <b-dropdown variant="-ternary btn--outline no--arr btn--dots btn--outline" size="-small" text="⋮" right>
      <b-dropdown-item-button v-if="showCancel && (!isPrinting || isIndustrialPrinter)" @click="handleOpenCancelJob">{{$t('printers.job.cancel')}}</b-dropdown-item-button>
      <b-dropdown-item-button v-if="showVirtualFinish && !isPrinting" @click="handleVirtualFinish">{{$t('printers.job.finish')}}</b-dropdown-item-button>
      <b-dropdown-item-button v-if="showVirtualFinish" @click="handleVirtualError">{{$t('printers.job.error')}}</b-dropdown-item-button>
      <b-dropdown-item-button v-if="showRestart" @click="handleRestart">{{$t('printers.job.restart')}}</b-dropdown-item-button>
      <b-dropdown-item-button v-if="showDetails" @click="handleOpenDetails">{{$t('printers.job.details')}}</b-dropdown-item-button>
      <b-dropdown-item-button v-if="showDownload">{{$t('printers.job.download')}}</b-dropdown-item-button>
      <b-dropdown-item-button v-if="showCopy" @click="handleCopy">{{$t('printers.job.copy')}}</b-dropdown-item-button>
      <b-dropdown-item-button v-if="showNotes" @click="openNotes = true">{{$t('printers.job.notes')}}</b-dropdown-item-button>
    </b-dropdown>

    <b-modal v-model="openDetails" modal-class="modal--edit modal--data modal--details">
      <job-details :printer-id="printer.printer_id" :job="job" :show-timelaps="detailsShowTimelaps"></job-details>
      <template v-slot:modal-footer="{ cancel, ok }">
        <div class="modal__footer--left">
          <b-button variant="-gray" @click="cancel()">
            {{$t('buttons.close')}}
          </b-button>
        </div>
        <div class="modal__footer--right">
          <b-button v-if="isGcodeFileType(job.file_type_id)" variant="-primary" @click="handleOpenToolpathViewer(ok)">
            {{$t('printers.modal.jobDetails.openToolpath')}}
          </b-button>
          <b-button v-else-if="isStlFileType(job.file_type_id)" variant="-primary" @click="handleOpenStlEditor(ok)">
            {{$t('printers.modal.jobDetails.openStlEditor')}}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal v-model="openNotes" modal-class="modal--notes" hide-footer>
      <job-notes :job-id="job.job_id"></job-notes>
    </b-modal>

    <b-modal ref="copyMovePopup" v-model="openCopyMove" modal-class="modal--clear" size="lg" hide-footer>
      <choose-printer-for-action modal-class="modal--clear" :job="job" :action="copyMoveAction" @close="handleCloseCopyMove"></choose-printer-for-action>
    </b-modal>

    <b-modal ref="restartPopup" v-model="openRestart" modal-class="modal--clear" size="lg" hide-footer>
      <choose-printer-for-action modal-class="modal--clear" :job="job" :printerId="printer.printer_id" action="restart" @close="handleCloseRestart"></choose-printer-for-action>
    </b-modal>

    <b-modal v-model="openCancelJob" size="lg">
      <confirm-job-cancel ref="cancelJob" :printer-id="printer.printer_id" :job-id="job.job_id"></confirm-job-cancel>
      <template v-slot:modal-footer="{ cancel, ok }">
        <div class="modal__footer--left">
          <b-button variant="-gray" @click="cancel()">
            {{$t('printers.modal.confirmJobCancel.back')}}
          </b-button>
        </div>
        <div class="modal__footer--right">
          <b-button variant="-primary" @click="handleCancelJob(ok)">
            {{$t('printers.modal.confirmJobCancel.cancelJob')}}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal v-model="openCancellationReason" size="lg">
      <cancellation-reason ref="cancellationReason" :job-id="job.job_id"></cancellation-reason>
      <template v-slot:modal-footer="{ cancel, ok }">
        <div class="modal__footer--left">
          <b-button variant="-gray" @click="cancel()">
            {{$t('buttons.cancel')}}
          </b-button>
        </div>
        <div class="modal__footer--right">
          <b-button variant="-primary" @click="handleSetCancelReason(ok)">
            {{$t('buttons.ok')}}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import {mapActions, mapGetters} from "vuex";
  import PrinterMixin from "@/mixins/printer";
  import FileMixin from "@/mixins/file";
  import UserMixin from "@/mixins/user";
  import ApplicationMixin from "@/mixins/application";

  import JobDetails from "../modal/JobDetails";
  import JobNotes from "../modal/JobNotes";
  import ChoosePrinterForAction from "../modal/ChoosePrinterForAction";
  import ConfirmJobCancel from "../modal/ConfirmJobCancel";
  import CancellationReason from "../modal/CancellationReason";

  export default {
    name: 'job-action-buttons',
    props: ['job', 'printer'],
    mixins: [PrinterMixin, FileMixin, UserMixin, ApplicationMixin],
    components: {
      ConfirmJobCancel,
      CancellationReason,
      ChoosePrinterForAction,
      JobDetails,
      JobNotes
    },
    data() {
      return {
        disableStart: false,
        disablePause: false,
        disableResume: false,
        disableCancel: false,
        disableVirtualFinish: false,

        openDetails: false,
        detailsShowTimelaps: false,
        openNotes: false,

        openCopyMove: false,
        copyMoveAction: '',

        openCancelJob: false,
        openCancellationReason: false,

        openRestart: false
      }
    },
    computed: {
      ...mapGetters(['getPrintSettings']),

      isIndustrialPrinter() {
        return this.printer.state === 'Virtual';
      },

      showPrint() {
        //Can start only jobs in queu
        if (this.job.job_status_id != 11) {
          return false;
        }
        //Check printer access level
        if (this.printer.printer_ownership_id >= 4) {
          return false;
        }
        //Check printer state
        if (this.printer.state !== 'idle' && this.printer.state !== 'Virtual') {
          return false;
        }
        //Check clear bed notification
        if (this.getPrintSettings && this.getPrintSettings.confirm_build_tray_is_empty) {
          if (this.printer.bed_not_clear || (this.printer.clear_bed_notification === 't' && this.printer.build_tray_not_clear === 't')) {
            return false;
          }
        }
        //Check print settings
        if (this.printer.printer_ownership_id == 3) {
          if (this.getPrintSettings && this.getPrintSettings.canprint_rs&2 && this.job.email!=window.myEmail) {
            return false;
          }
          if (this.getPrintSettings && this.getPrintSettings.canprint_rs&1) {
            const nextPrintJobId = this.getNextPrinterJobId(this.printer.jobs);
            if (nextPrintJobId != this.job.job_id) {
              return false;
            }
          }
        }

        return true;
      },
      showRestart() {
        return this.job.job_status_id > 40;
      },
      showPause() {
        return this.printer.printer_ownership_id < 4 && this.job.job_status_id == 21 && this.printer.state !== 'paused';
      },
      showResume() {
        return this.printer.printer_ownership_id < 4 && ((this.job.job_status_id == 21 && this.printer.state === 'paused') || this.job.job_status_id == 35);
      },
      showCancel() {
        if (this.job.job_status_id < 40 && this.job.hide_job !== 't') {
          if (this.printer.printer_ownership_id < 4 || this.job.job_status_id == 11 || (this.ptinter.printer_ownership_id == 4 && this.getPrintSettings && this.getPrintSettings.canqueue_im&1)) {
            if (this.printer.printer_ownership_id==3 && !this.isAdmin && this.getPrintSettings && this.getPrintSettings.canprint_rs&4 && this.job.email!=window.myEmail) {
              return false;
            } else {
              return true;
            }
          }
        }

        return false;
      },
      showVirtualFinish() {
        return this.printer.printer_ownership_id < 4 && this.isPrinting && this.isIndustrialPrinter;
      },
      showDetails() {
        return this.job.hide_job !== 't' && this.isGcodeFileType(this.job.file_type_id);
      },
      showNotes() {
        return true;//this.job.has_notes === 't';
      },
      showCopy() {
        return this.printer.printer_ownership_id < 4;
      },
      showDownload() {
        return this.printer.virtual === 't';
      },
      isPrinting() {
        return this.job.job_status_id == 21 || this.job.job_status_id == 35;
      },
      isBuildTrayIsClear() {
        return this.buildTrayIsClear(this.printer);
      },
      startButtonAttributes() {
        if (!this.isBuildTrayIsClear) {
          return {'v-b-tooltip.hover': true, 'title': this.$t('printers.job.buildTrayIsNotClearTooltip')};
        }
        return {};
      }
    },
    methods: {
      ...mapActions(['startJob', 'printingPause', 'printingResume', 'printingVirtualFinish', 'printingVirtualError']),
      handlePrint() {
        this.disableStart = true;
        this.startJob({printerId: this.printer.printer_id, jobId: this.job.job_id}).then(({data}) => {
          this.disableStart = false;
          if (!data.result) {
            this.msgBoxOk(data.message, {title: this.$t('printers.job.printErrorTitle')});
          }
        });
      },
      handlePause() {
        this.msgBoxConfirm(this.$t('printing.job.confirmPause'))
          .then(value => {
            if (!value) return;
            this.disablePause = true;
            this.printingPause(this.printer.printer_id).then(() => {
              this.disablePause = false;
            });
          });
      },
      handleResume() {
        this.disableResume = true;
        this.printingResume(this.printer.printer_id).then(() => {
          this.disableResume = false;
        })
      },
      handleVirtualFinish() {
        this.disableVirtualFinish = true;
        this.printingVirtualFinish(this.printer.printer_id).then(() => {
          this.disableVirtualFinish = false;
        });
      },
      handleVirtualError() {
        this.msgBoxConfirm(this.$t('printing.job.confirmPrintError'))
          .then(() => {
            this.printingVirtualError(this.printer.printer_id);
          });
      },
      handleOpenDetails() {
        this.detailsShowTimelaps = false;
        this.openDetails = true;
      },
      handleOpenTimelaps() {
        this.detailsShowTimelaps = true;
        this.openDetails = true;
      },
      handleCopy() {
        this.copyMoveAction = 'copy';
        this.openCopyMove = true;
      },
      handleCloseCopyMove() {
        this.$refs['copyMovePopup'].hide();
      },
      handleOpenCancelJob() {
        this.openCancelJob = true;
      },
      handleCancelJob(ok) {
        this.$refs.cancelJob.handleCancelJob(() => {
          this.openCancellationReason = true;
        });
        ok();
      },
      handleSetCancelReason(ok) {
        this.$refs.cancellationReason.handleSetCancelReason();
        ok();
      },
      handleOpenToolpathViewer(ok) {
        ok();
        this.oldApps_showToolpathViewer(this.job.file_id, this.job.filename);
      },
      handleOpenStlEditor(ok) {
        ok();
        this.oldApps_showStlEditor(this.job.file_id, this.job.filename);
      },
      handleRestart() {
        this.openRestart = true;
      },
      handleCloseRestart() {
        this.$refs['restartPopup'].hide();
      }
    }
  }
</script>
